// Currency, UOM and growth-ratio formatting for AABI. Kept separate from
// common/card's formatNumber because UOM is a unit-less integer (§4.1, §5.5) and
// growth is a signed ratio that must render "New" rather than Infinity/NaN (D11,
// §7.4).

// Full precision, cents included. Reserved for tooltips and accessible labels
// — the places where someone has asked for the exact figure. Everything on the
// page surface uses formatCompactCurrency (aabiViewModel) or, in the Data
// grid, formatWholeCurrency.
export const formatCurrency = (value) => {
  const numericValue = Number(value ?? 0);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return safeValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Exact dollars, no cents. The Data grid is the sortable, exportable surface
// people reconcile against invoices, so it keeps real values rather than
// compact ones — but cents on a six-figure number are noise.
export const formatWholeCurrency = (value) => {
  const numericValue = Number(value ?? 0);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return safeValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// UOM is always an integer (§4.1) — no decimals, ever. `null`/`undefined` (not
// yet set, D11) renders as 0, same as a genuine 0.
export const formatUOM = (value) => {
  const numericValue = Number(value ?? 0);
  const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
  return safeValue.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

// growth is a signed ratio (0.152 === +15.2%), null when the prior period was 0
// (D11 / §7.4) — callers render "New" for null, never Infinity/NaN.
export const formatGrowth = (growth) => {
  if (growth === null || growth === undefined || !Number.isFinite(growth)) {
    return "New";
  }
  const sign = growth > 0 ? "+" : growth < 0 ? "-" : "";
  const percent = Math.abs(growth * 100).toFixed(1);
  return `${sign}${percent}%`;
};

// The ChangeIndicator variant matching a growth ratio (change-indicator.js).
export const growthVariant = (growth) => {
  if (growth === null || growth === undefined || !Number.isFinite(growth)) {
    return "blue";
  }
  if (growth > 0) return "positive";
  if (growth < 0) return "negative";
  return "white";
};
